/**
 * CapacityAssignmentsTab Component
 *
 * Feature: 126-client-assignment-views (T008)
 * Description: Tab component showing capacity assignments for a client.
 *   Displays assignment table with capacity type, SKU, region, status, dates.
 *   Supports "Show history" toggle, pagination, and click-through to CapacityDetailModal.
 * Constitution: v1.12.0 — Principle XXXVII (Code Header Standards), IV (Multi-Type)
 */

import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Cpu, ChevronRight, AlertCircle } from 'lucide-react';
import { clientApi } from '../../services/clientApi';
import {
  type CapacityAssignmentView,
  CAPACITY_TYPE_LABELS,
  type CapacityType,
} from '@shared/types/client-detail';
import { CapacityDetailModal } from '../operations/CapacityDetailModal';
import { formatEasternDate } from '@/utils/dateFormatter';
import { useCapacityDetail } from '../../services/operations/capacitiesService';
import { DualScrollTable, usePagination, PaginationControls } from '../common/TableControls';

/** Status badge color mapping */
const STATUS_COLORS: Record<string, string> = {
  Active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  Suspended: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  Deleting: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  Deleted: 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]',
};

/** Capacity type badge colors */
const TYPE_COLORS: Record<CapacityType, string> = {
  Fabric:    'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  PowerBI:   'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  MsManaged: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  Unknown:   'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]',
};

interface CapacityAssignmentsTabProps {
  clientId: string;
}

export const CapacityAssignmentsTab: React.FC<CapacityAssignmentsTabProps> = ({ clientId }) => {
  // State
  const [showHistory, setShowHistory] = useState(false);
  const [selectedCapacityId, setSelectedCapacityId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch assignments
  const {
    data: assignments = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['client', clientId, 'capacities', showHistory],
    queryFn: () => clientApi.getCapacityAssignments(clientId, showHistory),
    staleTime: 30_000,
    enabled: !!clientId,
  });

  // Fetch the FULL capacity entity for the modal (same source as the operations
  // inventory page) so it shows complete data — status, health, region, workspaces.
  const { data: capacityDetail, isLoading: isCapacityLoading } = useCapacityDetail(
    isModalOpen ? selectedCapacityId : null
  );

  // Pagination: one paginator over every assignment (design blueprint). The old
  // 25-row "Load more" stacked under it and made the footer count only the loaded rows.
  const totalItems = assignments.length;
  const {
    page: tablePage,
    setPage: setTablePage,
    pageSize: tablePageSize,
    changeSize: changeTableSize,
    total: tableTotal,
    totalPages: tableTotalPages,
    pageItems,
    start: tableStart,
  } = usePagination(assignments);

  // Handlers
  const handleRowClick = useCallback((assignment: CapacityAssignmentView) => {
    // Don't open modal for deleted capacities
    if (!assignment.capacityName) {
      return;
    }
    setSelectedCapacityId(assignment.capacityId);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedCapacityId(null);
  }, []);

  const handleToggleHistory = useCallback(() => {
    setShowHistory((prev) => !prev);
    setTablePage(1); // Reset pagination when toggling
  }, [setTablePage]);

  // Format date for display
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '—';
    return formatEasternDate(dateString);
  };

  // Get capacity type label
  const getTypeLabel = (type: CapacityType | null): string => {
    if (!type) return '—';
    return CAPACITY_TYPE_LABELS[type] || type;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent)]" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-red-600 dark:text-red-400">
        <AlertCircle className="w-8 h-8 mb-2" />
        <p>Failed to load capacity assignments</p>
        <p className="text-sm text-[var(--color-text-secondary)]">{(error as Error).message}</p>
      </div>
    );
  }

  // Empty state
  if (assignments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-[var(--color-text-secondary)]">
        <Cpu className="w-12 h-12 mb-4 opacity-50" />
        <p className="text-lg font-medium">No capacities assigned to this client.</p>
        <p className="text-sm mt-1">
          <a
            href={`/admin/client-assignments?client=${clientId}`}
            className="text-[var(--color-accent)] hover:opacity-90"
          >
            Manage capacity assignments
          </a>
        </p>
        {/* History toggle for empty active list */}
        <button
          onClick={handleToggleHistory}
          className="mt-4 text-sm text-[var(--color-accent)] hover:opacity-90"
        >
          {showHistory ? 'Hide history' : 'Show history'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls: Show history toggle */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--color-text-secondary)]">
          {totalItems} {totalItems === 1 ? 'assignment' : 'assignments'}
          {showHistory ? ' (including history)' : ' (active only)'}
        </p>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={showHistory}
            onChange={handleToggleHistory}
            className="rounded border-[var(--color-border)] text-[var(--color-accent)] focus:ring-[var(--color-accent)]"
          />
          <span className="text-[var(--color-text-secondary)]">Show history</span>
        </label>
      </div>

      {/* Assignments table */}
      <div className="rounded-lg border border-[var(--color-border)]">
        <DualScrollTable>
        <table className="w-full text-sm">
          <thead className="border-b border-[var(--color-border)] bg-[var(--color-bg-subtle)]">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                Capacity Name
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                Type
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                SKU
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                Region
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                Status
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                Effective Date
              </th>
              {showHistory && (
                <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                  End Date
                </th>
              )}
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                Primary
              </th>
              <th className="px-4 py-3 w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border)]">
            {pageItems.map((assignment) => {
              const isDeleted = !assignment.capacityName;
              const isEnded = !assignment.isActive;

              return (
                <tr
                  key={assignment.id}
                  onClick={() => handleRowClick(assignment)}
                  className={`
                    ${isDeleted ? 'bg-[var(--color-bg-subtle)]' : 'hover:bg-[var(--color-bg-subtle)] cursor-pointer'}
                    ${isEnded ? 'opacity-60' : ''}
                  `}
                >
                  {/* Capacity Name */}
                  <td className="px-4 py-3 font-medium text-[var(--color-text-primary)]">
                    <div className="flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-[var(--color-text-secondary)]" />
                      {isDeleted ? (
                        <span className="text-red-500 dark:text-red-400">(Deleted)</span>
                      ) : (
                        <span>{assignment.capacityName}</span>
                      )}
                    </div>
                  </td>

                  {/* Type */}
                  <td className="px-4 py-3">
                    {assignment.capacityType && (
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_COLORS[assignment.capacityType] || 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'}`}
                      >
                        {getTypeLabel(assignment.capacityType)}
                      </span>
                    )}
                    {!assignment.capacityType && (
                      <span className="text-[var(--color-text-secondary)]">—</span>
                    )}
                  </td>

                  {/* SKU */}
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {assignment.sku || '—'}
                  </td>

                  {/* Region */}
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {assignment.region || '—'}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    {assignment.capacityStatus && (
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[assignment.capacityStatus] || 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'}`}
                      >
                        {assignment.capacityStatus}
                      </span>
                    )}
                    {!assignment.capacityStatus && (
                      <span className="text-[var(--color-text-secondary)]">—</span>
                    )}
                  </td>

                  {/* Effective Date */}
                  <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                    {formatDate(assignment.effectiveDate)}
                  </td>

                  {/* End Date (only in history mode) */}
                  {showHistory && (
                    <td className="px-4 py-3 text-[var(--color-text-secondary)]">
                      {formatDate(assignment.endDate)}
                    </td>
                  )}

                  {/* Primary flag */}
                  <td className="px-4 py-3 text-center">
                    {assignment.isPrimary && (
                      <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                        Primary
                      </span>
                    )}
                  </td>

                  {/* Click indicator */}
                  <td className="px-4 py-3">
                    {!isDeleted && (
                      <ChevronRight className="w-4 h-4 text-[var(--color-text-secondary)]" />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </DualScrollTable>
        <PaginationControls page={tablePage} pageSize={tablePageSize} total={tableTotal} totalPages={tableTotalPages} start={tableStart} shown={pageItems.length} onPage={setTablePage} onPageSize={changeTableSize} />
      </div>

      {/* Capacity Detail Modal */}
      <CapacityDetailModal
        capacity={capacityDetail ?? null}
        isLoading={isCapacityLoading}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default CapacityAssignmentsTab;
